/**
 * ContextPad — bpmn-js style floating action pad for the DMN DRD editor.
 *
 * Mirrors the epx-flow ContextPad API/CSS so both editors feel like one
 * product family.  Key differences vs the BPMN version:
 *  - APPEND_TABS driven by DMN types + DMN connection rules
 *  - Edge type is auto-resolved via ConnectionRules.resolveEdgeType()
 *  - "Editar reglas" shortcut for Decision nodes
 */

import { SHAPE_DEFAULTS } from './shapes/index';

// ─── SVG icons (DMN shapes, matching the palette miniatures) ─────────────────

const ICONS = {
  decision: `<svg width="20" height="14" viewBox="0 0 20 14">
    <rect x="1" y="1" width="18" height="12" fill="#e6f7ff" stroke="#1890ff" stroke-width="1.5"/>
  </svg>`,

  inputData: `<svg width="20" height="14" viewBox="0 0 20 14">
    <rect x="1" y="1" width="18" height="12" rx="6" ry="6" fill="#e6fffb" stroke="#13c2c2" stroke-width="1.5"/>
  </svg>`,

  bkm: `<svg width="20" height="14" viewBox="0 0 20 14">
    <path d="M 4 1 L 19 1 L 19 10 L 16 13 L 1 13 L 1 4 Z" fill="#f9f0ff" stroke="#722ed1" stroke-width="1.5"/>
  </svg>`,

  knowledgeSource: `<svg width="20" height="14" viewBox="0 0 20 14">
    <path d="M 1 1 L 19 1 L 19 10 Q 15 8 10 10 T 1 10 Z" fill="#fffbe6" stroke="#d48806" stroke-width="1.5"/>
  </svg>`,

  textAnnotation: `<svg width="20" height="14" viewBox="0 0 20 14">
    <path d="M 6 1 L 1 1 L 1 13 L 6 13" fill="none" stroke="#8c8c8c" stroke-width="1.5"/>
    <line x1="4" y1="5" x2="16" y2="5" stroke="#8c8c8c" stroke-width="0.8"/>
    <line x1="4" y1="8" x2="13" y2="8" stroke="#8c8c8c" stroke-width="0.8"/>
    <line x1="4" y1="11" x2="10" y2="11" stroke="#8c8c8c" stroke-width="0.8"/>
  </svg>`,

  edit: `<svg width="16" height="16" viewBox="0 0 16 16">
    <path d="M 11.5 1.5 L 14.5 4.5 L 5.5 13.5 L 1.5 14.5 L 2.5 10.5 Z"
          fill="none" stroke="#555" stroke-width="1.2" stroke-linejoin="round"/>
    <path d="M 10 3 L 13 6" fill="none" stroke="#555" stroke-width="1"/>
  </svg>`,

  table: `<svg width="16" height="16" viewBox="0 0 16 16">
    <rect x="1" y="1" width="14" height="11" rx="1" fill="none" stroke="#1890ff" stroke-width="1.2"/>
    <rect x="1" y="1" width="14" height="4" rx="1" fill="#e6f7ff" stroke="#1890ff" stroke-width="1.2"/>
    <line x1="6" y1="1" x2="6" y2="12" stroke="#1890ff" stroke-width="0.8"/>
    <line x1="11" y1="1" x2="11" y2="12" stroke="#1890ff" stroke-width="0.8"/>
    <line x1="1" y1="7.5" x2="15" y2="7.5" stroke="#1890ff" stroke-width="0.8"/>
  </svg>`,

  expression: `<svg width="16" height="16" viewBox="0 0 16 16">
    <text x="1" y="12" font-family="monospace" font-size="10" font-style="italic" fill="#722ed1">f(x)</text>
  </svg>`,

  addNode: `<svg width="16" height="16" viewBox="0 0 16 16">
    <rect x="1" y="2" width="14" height="10" rx="2" fill="none" stroke="#555" stroke-width="1.2"/>
    <path d="M8 5v4M6 7h4" stroke="#555" stroke-width="1.5" stroke-linecap="round"/>
  </svg>`,

  delete: `<svg width="16" height="16" viewBox="0 0 16 16">
    <path d="M 4 4 L 12 12 M 12 4 L 4 12" stroke="#f5222d" stroke-width="2" stroke-linecap="round"/>
  </svg>`,
};

// ─── Append tabs per source type ──────────────────────────────────────────────
// Each entry: { type, icon, label, shortLabel }
// Only valid targets per source according to DMN 1.3 are shown.

const APPEND_FOR = {
  'dmn:Decision': [
    { type: 'dmn:Decision',               icon: 'decision',       label: 'Decision',              shortLabel: 'Decision' },
    { type: 'dmn:KnowledgeSource',        icon: 'knowledgeSource', label: 'Knowledge Source',      shortLabel: 'K. Source' },
    { type: 'dmn:TextAnnotation',         icon: 'textAnnotation',  label: 'Anotación de texto',    shortLabel: 'Anotación' },
  ],
  'dmn:InputData': [
    { type: 'dmn:Decision',               icon: 'decision',        label: 'Decision',              shortLabel: 'Decision' },
    { type: 'dmn:KnowledgeSource',        icon: 'knowledgeSource', label: 'Knowledge Source',      shortLabel: 'K. Source' },
    { type: 'dmn:TextAnnotation',         icon: 'textAnnotation',  label: 'Anotación de texto',    shortLabel: 'Anotación' },
  ],
  'dmn:BusinessKnowledgeModel': [
    { type: 'dmn:Decision',               icon: 'decision',        label: 'Decision',              shortLabel: 'Decision' },
    { type: 'dmn:BusinessKnowledgeModel', icon: 'bkm',             label: 'Business Knowledge Model', shortLabel: 'BKM' },
    { type: 'dmn:TextAnnotation',         icon: 'textAnnotation',  label: 'Anotación de texto',    shortLabel: 'Anotación' },
  ],
  'dmn:KnowledgeSource': [
    { type: 'dmn:Decision',               icon: 'decision',        label: 'Decision',              shortLabel: 'Decision' },
    { type: 'dmn:BusinessKnowledgeModel', icon: 'bkm',             label: 'Business Knowledge Model', shortLabel: 'BKM' },
    { type: 'dmn:KnowledgeSource',        icon: 'knowledgeSource', label: 'Knowledge Source',      shortLabel: 'K. Source' },
    { type: 'dmn:TextAnnotation',         icon: 'textAnnotation',  label: 'Anotación de texto',    shortLabel: 'Anotación' },
  ],
};

// ─── CSS (injected once, mirrors epx-flow ContextPad CSS) ────────────────────

let _cssInjected = false;

function injectCSS() {
  if (_cssInjected) return;
  _cssInjected = true;
  const style = document.createElement('style');
  style.id = 'epx-dmn-ctx-pad-v2-styles';
  style.textContent = `
    /* ── Context pad ── */
    .epx-dmn-ctx-pad-v2 {
      position: absolute;
      display: flex;
      flex-direction: column;
      gap: 2px;
      padding: 4px;
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      box-shadow: 0 4px 16px rgba(0,0,0,.12), 0 1px 4px rgba(0,0,0,.06);
      z-index: 200;
      pointer-events: auto;
    }
    .epx-dmn-ctx-pad-v2 .epx-dcp-divider {
      width: 100%;
      height: 1px;
      background: #f1f5f9;
      margin: 2px 0;
    }
    .epx-dmn-ctx-pad-v2 .epx-dcp-btn {
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px solid transparent;
      border-radius: 6px;
      background: transparent;
      cursor: pointer;
      padding: 0;
      transition: background .1s, border-color .1s;
      position: relative;
      color: #475569;
    }
    .epx-dmn-ctx-pad-v2 .epx-dcp-btn:hover {
      background: #eff6ff;
      border-color: #bfdbfe;
      color: #1d4ed8;
    }
    .epx-dmn-ctx-pad-v2 .epx-dcp-btn.epx-dcp-active {
      background: #eff6ff;
      border-color: #3b82f6;
    }
    .epx-dmn-ctx-pad-v2 .epx-dcp-btn.epx-dcp-danger:hover {
      background: #fef2f2;
      border-color: #fecaca;
      color: #dc2626;
    }
    .epx-dmn-ctx-pad-v2 .epx-dcp-btn[data-tooltip]:hover::after {
      content: attr(data-tooltip);
      position: absolute;
      left: calc(100% + 8px);
      top: 50%;
      transform: translateY(-50%);
      background: #1e293b;
      color: #f1f5f9;
      padding: 3px 8px;
      border-radius: 4px;
      font-size: 10px;
      white-space: nowrap;
      z-index: 300;
      pointer-events: none;
      font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
    }

    /* ── Append panel popover ── */
    .epx-dmn-append-panel {
      position: absolute;
      width: 240px;
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 10px;
      box-shadow: 0 8px 32px rgba(0,0,0,.14), 0 2px 8px rgba(0,0,0,.07);
      z-index: 250;
      overflow: hidden;
      font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
      font-size: 12px;
    }
    .epx-dap-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 10px 14px;
      border-bottom: 1px solid #f1f5f9;
    }
    .epx-dap-title {
      font-size: 10px;
      font-weight: 700;
      color: #0f172a;
      letter-spacing: .07em;
      text-transform: uppercase;
    }
    .epx-dap-close {
      width: 22px;
      height: 22px;
      border-radius: 5px;
      border: none;
      background: transparent;
      color: #94a3b8;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 14px;
      padding: 0;
    }
    .epx-dap-close:hover { background: #f1f5f9; color: #475569; }
    .epx-dap-body {
      padding: 8px 9px 10px;
    }
    .epx-dap-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 4px;
    }
    .epx-dap-item {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
      padding: 7px 4px 6px;
      border-radius: 7px;
      cursor: pointer;
      border: 1.5px solid transparent;
      transition: background .1s, border-color .1s;
      user-select: none;
    }
    .epx-dap-item:hover {
      background: #f8fafc;
      border-color: #e2e8f0;
    }
    .epx-dap-icon {
      width: 36px;
      height: 26px;
      border-radius: 5px;
      border: 1.5px solid #e2e8f0;
      background: #f8fafc;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: border-color .1s, background .1s;
    }
    .epx-dap-item:hover .epx-dap-icon {
      border-color: #bfdbfe;
      background: #f0f9ff;
    }
    .epx-dap-icon svg { width: 20px; height: 14px; }
    .epx-dap-item-label {
      font-size: 9px;
      color: #64748b;
      text-align: center;
      line-height: 1.25;
    }
  `;
  document.head.appendChild(style);
}

// ─── ContextPad class ─────────────────────────────────────────────────────────

const NEXT_X = 220;  // default horizontal offset for appended nodes

export class ContextPad {
  /**
   * @param {import('@logicflow/core').default} lf
   * @param {import('./ConnectionRules').ConnectionRules} connectionRules
   * @param {object} eventHandlers  – same map used in DrdEditor
   */
  constructor(lf, connectionRules, eventHandlers = {}) {
    this._lf = lf;
    this._rules = connectionRules;
    this._handlers = eventHandlers;
    this._activeNode = null;
    this._padEl = null;
    this._appendPanelEl = null;
    this._appendPanelClickHandler = null;
    this._addNodeBtn = null;

    injectCSS();
    this._createPad();
    this._bind();
  }

  _createPad() {
    this._padEl = document.createElement('div');
    this._padEl.className = 'epx-dmn-ctx-pad-v2';
    this._padEl.style.display = 'none';
  }

  _bind() {
    this._lf.on('node:click', ({ data }) => this._show(data));
    this._lf.on('blank:click', () => this._hide());
    this._lf.on('node:delete', () => this._hide());
    this._lf.on('node:drag', () => this._hide());
    this._lf.on('graph:transform', () => {
      if (this._activeNode) {
        this._reposition();
        if (this._appendPanelEl) this._positionAppendPanel(this._activeNode);
      }
    });
  }

  _show(nodeData) {
    this._activeNode = nodeData;
    this._hideAppendPanel();
    this._buildButtons(nodeData);
    this._reposition();
    this._padEl.style.display = 'flex';

    const container = this._lf.graphModel.rootEl;
    if (container && !container.contains(this._padEl)) {
      container.appendChild(this._padEl);
    }
  }

  _hide() {
    this._activeNode = null;
    this._hideAppendPanel();
    this._padEl.style.display = 'none';
  }

  _reposition() {
    if (!this._activeNode) return;
    const model = this._lf.graphModel.getElement(this._activeNode.id);
    if (!model) return;

    const w = model.width || 140;
    const h = model.height || 60;
    const canvasX = this._activeNode.x + w / 2 + 8;
    const canvasY = this._activeNode.y - h / 2 - 4;
    const [htmlX, htmlY] = this._lf.graphModel.transformModel.CanvasPointToHtmlPoint([canvasX, canvasY]);

    this._padEl.style.left = htmlX + 'px';
    this._padEl.style.top = htmlY + 'px';
  }

  _buildButtons(nodeData) {
    this._padEl.innerHTML = '';
    this._addNodeBtn = null;
    const type = nodeData.type;

    const appendItems = APPEND_FOR[type];
    if (appendItems && appendItems.length) {
      this._addNodeBtn = this._addBtn(ICONS.addNode, 'Agregar nodo', () => {
        this._toggleAppendPanel(nodeData);
      }, false, true);
    }

    if (type === 'dmn:Decision' || type === 'dmn:BusinessKnowledgeModel') {
      const logicType = nodeData.properties?.logicType;
      const isTable = logicType === 'dmn:DecisionTable';
      const icon = isTable ? ICONS.table : (logicType ? ICONS.expression : ICONS.table);
      const tooltip = isTable ? 'Editar tabla de decisión' : (logicType ? 'Editar expresión' : 'Editar lógica');
      this._addBtn(icon, tooltip, () => {
        this._emit('node.openTable', { element: nodeData });
        this._hide();
      });
    }

    this._addDivider();
    this._addBtn(ICONS.edit, 'Editar nombre', () => this._editNode(nodeData));
    this._addBtn(ICONS.delete, 'Eliminar', () => this._deleteNode(nodeData), true);
  }

  _addBtn(iconSvg, tooltip, onClick, isDanger = false, toggleable = false) {
    const btn = document.createElement('button');
    btn.className = 'epx-dcp-btn' + (isDanger ? ' epx-dcp-danger' : '');
    btn.innerHTML = iconSvg;
    btn.setAttribute('data-tooltip', tooltip);
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      onClick();
      if (toggleable) btn.classList.toggle('epx-dcp-active', !!this._appendPanelEl);
    });
    this._padEl.appendChild(btn);
    return btn;
  }

  _addDivider() {
    const d = document.createElement('div');
    d.className = 'epx-dcp-divider';
    this._padEl.appendChild(d);
  }

  // ── Append panel ────────────────────────────────────────────────────────────

  _toggleAppendPanel(nodeData) {
    if (this._appendPanelEl) {
      this._hideAppendPanel();
      if (this._addNodeBtn) this._addNodeBtn.classList.remove('epx-dcp-active');
      return;
    }
    this._showAppendPanel(nodeData);
  }

  _showAppendPanel(nodeData) {
    const items = APPEND_FOR[nodeData.type] || [];
    if (!items.length) return;

    const panel = this._buildPanel(nodeData, items);
    this._appendPanelEl = panel;

    const container = this._lf.graphModel.rootEl;
    container.appendChild(panel);
    this._positionAppendPanel(nodeData);
    if (this._addNodeBtn) this._addNodeBtn.classList.add('epx-dcp-active');

    setTimeout(() => {
      this._appendPanelClickHandler = (e) => {
        if (
          this._appendPanelEl &&
          !this._appendPanelEl.contains(e.target) &&
          !this._padEl.contains(e.target)
        ) {
          this._hideAppendPanel();
          if (this._addNodeBtn) this._addNodeBtn.classList.remove('epx-dcp-active');
        }
      };
      document.addEventListener('click', this._appendPanelClickHandler);
    }, 0);
  }

  _hideAppendPanel() {
    if (this._appendPanelEl && this._appendPanelEl.parentNode) {
      this._appendPanelEl.parentNode.removeChild(this._appendPanelEl);
    }
    this._appendPanelEl = null;
    if (this._appendPanelClickHandler) {
      document.removeEventListener('click', this._appendPanelClickHandler);
      this._appendPanelClickHandler = null;
    }
  }

  _buildPanel(nodeData, items) {
    const panel = document.createElement('div');
    panel.className = 'epx-dmn-append-panel';

    // Header
    const hdr = document.createElement('div');
    hdr.className = 'epx-dap-header';
    const title = document.createElement('div');
    title.className = 'epx-dap-title';
    title.textContent = 'Agregar Elemento';
    hdr.appendChild(title);
    const closeBtn = document.createElement('button');
    closeBtn.className = 'epx-dap-close';
    closeBtn.innerHTML = '&#x2715;';
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this._hideAppendPanel();
      if (this._addNodeBtn) this._addNodeBtn.classList.remove('epx-dcp-active');
    });
    hdr.appendChild(closeBtn);
    panel.appendChild(hdr);

    // Grid of items
    const body = document.createElement('div');
    body.className = 'epx-dap-body';
    const grid = document.createElement('div');
    grid.className = 'epx-dap-grid';

    for (const item of items) {
      const el = document.createElement('div');
      el.className = 'epx-dap-item';

      const iconBox = document.createElement('div');
      iconBox.className = 'epx-dap-icon';
      iconBox.innerHTML = ICONS[item.icon] || '';
      el.appendChild(iconBox);

      const lbl = document.createElement('div');
      lbl.className = 'epx-dap-item-label';
      lbl.textContent = item.shortLabel;
      el.appendChild(lbl);

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        this._appendNode(item.type, nodeData);
        this._hideAppendPanel();
        if (this._addNodeBtn) this._addNodeBtn.classList.remove('epx-dcp-active');
      });
      grid.appendChild(el);
    }

    body.appendChild(grid);
    panel.appendChild(body);
    return panel;
  }

  _positionAppendPanel(nodeData) {
    if (!this._appendPanelEl) return;
    const model = this._lf.graphModel.getElement(nodeData.id);
    if (!model) return;

    const w = model.width || 140;
    const h = model.height || 60;
    const canvasX = nodeData.x + w / 2 + 48;
    const canvasY = nodeData.y - h / 2 - 4;
    const [htmlX, htmlY] = this._lf.graphModel.transformModel.CanvasPointToHtmlPoint([canvasX, canvasY]);

    const container = this._lf.graphModel.rootEl;
    const containerRect = container.getBoundingClientRect();
    const panelW = 240;
    const panelH = 180;

    let left = htmlX;
    let top = htmlY;

    if (left + panelW > containerRect.width - 8) {
      const [flippedX] = this._lf.graphModel.transformModel.CanvasPointToHtmlPoint(
        [nodeData.x - w / 2 - panelW - 48, canvasY]
      );
      left = flippedX;
    }
    if (top + panelH > containerRect.height - 8) top = Math.max(8, containerRect.height - panelH - 8);
    if (top < 8) top = 8;

    this._appendPanelEl.style.left = left + 'px';
    this._appendPanelEl.style.top = top + 'px';
  }

  _appendNode(targetType, sourceData) {
    const lf = this._lf;
    const sourceModel = lf.graphModel.getElement(sourceData.id);
    if (!sourceModel) return;

    const defaults = SHAPE_DEFAULTS[targetType] || { width: 140, height: 60 };

    let targetX = sourceData.x + NEXT_X;
    let targetY = sourceData.y;

    // Anti-overlap: shift down if occupied
    for (let attempt = 0; attempt < 10; attempt++) {
      const area = lf.getAreaElement
        ? lf.getAreaElement([targetX - 80, targetY - 40], [targetX + 80, targetY + 40])
        : [];
      if (!area || !area.length) break;
      targetY += 90;
    }

    const newNode = lf.addNode({
      type: targetType,
      x: targetX,
      y: targetY,
      properties: {
        name: '',
        width: defaults.width,
        height: defaults.height,
      },
    });

    if (newNode) {
      const edgeType = this._rules
        ? this._rules.resolveEdgeType(sourceData.type, targetType)
        : 'dmn:InformationRequirement';

      if (edgeType) {
        const newEdgeId = `edge_ctx_${newNode.id}`;
        lf.addEdge({
          id: newEdgeId,
          sourceNodeId: sourceData.id,
          targetNodeId: newNode.id,
          type: edgeType,
        });
        const em = lf.getEdgeModelById(newEdgeId);
        if (em) {
          if (typeof em.setAnchors === 'function') em.setAnchors();
          if (typeof em.initPoints === 'function') em.initPoints();
        }
      }
    }

    this._hide();
  }

  _editNode(nodeData) {
    this._hide();
    const model = this._lf.graphModel.getElement(nodeData.id);
    if (!model) return;

    // Nodes with custom SVG text use properties.name — open inline HTML editor
    if (model.text && model.text.editable === false) {
      this._editCustomTextNode(model, nodeData);
      return;
    }
    this._lf.graphModel.editText(nodeData.id);
  }

  _editCustomTextNode(model, nodeData) {
    const lf = this._lf;
    const tm = lf.graphModel.transformModel;
    const [htmlX, htmlY] = tm.CanvasPointToHtmlPoint([model.x, model.y]);

    const input = document.createElement('input');
    input.type = 'text';
    input.value = (model.properties && model.properties.name) || '';
    input.style.cssText =
      'position:absolute;transform:translate(-50%,-50%);' +
      `left:${htmlX}px;top:${htmlY}px;` +
      `min-width:${Math.max(40, (model.width || 100) - 20)}px;` +
      'padding:2px 4px;border:1px solid #1890ff;border-radius:3px;' +
      'font:12px "Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif;' +
      'text-align:center;outline:none;z-index:1000;background:#fff;';

    const container = lf.container;
    container.appendChild(input);
    input.focus();
    input.select();

    let committed = false;
    const commit = () => {
      if (committed) return;
      committed = true;
      const val = input.value.trim();
      if (input.parentNode) input.parentNode.removeChild(input);
      lf.setProperties(nodeData.id, { name: val });
      lf.emit('text:update', { data: { ...nodeData, text: { value: val } } });
    };
    const cancel = () => {
      if (committed) return;
      committed = true;
      if (input.parentNode) input.parentNode.removeChild(input);
    };

    input.addEventListener('blur', commit);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); commit(); }
      else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
    });
  }

  _deleteNode(nodeData) {
    this._hide();
    try { this._lf.deleteNode(nodeData.id); }
    catch (_) { this._lf.deleteElement && this._lf.deleteElement(nodeData.id); }
  }

  _emit(event, payload) {
    const list = this._handlers[event];
    if (!list) return;
    list.forEach((h) => { try { h(payload); } catch (e) { console.error(e); } });
  }

  destroy() {
    this._hideAppendPanel();
    if (this._padEl && this._padEl.parentNode) {
      this._padEl.parentNode.removeChild(this._padEl);
    }
  }
}
